import { tokens } from '@/design';
import type { ProfileStats } from './fixtures';

export interface ProfileCursorReadoutProps {
  readonly station: string | null;
  readonly chainage: number | null;
  readonly elevation: number | null;
  readonly units: string;
  readonly exaggeration: number;
  readonly stats: ProfileStats | null;
}

export function ProfileCursorReadout({
  station,
  chainage,
  elevation,
  units,
  exaggeration,
  stats,
}: ProfileCursorReadoutProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={
        station
          ? `Station ${station}; chainage ${chainage} ${units}; elevation ${elevation} ${units}; exaggeration ${exaggeration}×`
          : 'No station selected'
      }
      data-testid="profile-cursor-readout"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[1],
        padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
        fontFamily: tokens.font.family.mono,
        fontSize: tokens.font.role.engMono.size,
        color: tokens.color.ink.primary,
        background: tokens.color.surface[1],
        borderTop: tokens.border.thin,
        minHeight: tokens.layout.bottomBarHeight,
        justifyContent: 'center',
      }}
    >
      <div style={{ display: 'flex', gap: tokens.space[4], flexWrap: 'nowrap' }}>
        <span>
          <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 14, verticalAlign: 'middle' }}>
            pin_drop
          </span>{' '}
          {station ?? '—'}
        </span>
        <span>
          <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 14, verticalAlign: 'middle' }}>
            straighten
          </span>{' '}
          {chainage != null ? `${chainage} ${units}` : '—'}
        </span>
        <span>
          <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 14, verticalAlign: 'middle' }}>
            vertical_align_bottom
          </span>{' '}
          {elevation != null ? `${elevation.toFixed(1)} ${units}` : '—'}
        </span>
        <span>
          <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 14, verticalAlign: 'middle' }}>
            height
          </span>{' '}
          {exaggeration}×
        </span>
      </div>

      {stats && (
        <div
          style={{
            display: 'flex',
            gap: tokens.space[3],
            flexWrap: 'nowrap',
            color: tokens.color.ink.secondary,
          }}
        >
          <span>min {stats.min.toFixed(1)}</span>
          <span>·</span>
          <span>max {stats.max.toFixed(1)}</span>
          <span>·</span>
          <span>mean {stats.mean.toFixed(1)}</span>
          <span>·</span>
          <span>P10 {stats.p10.toFixed(1)}</span>
          <span>·</span>
          <span>P50 {stats.p50.toFixed(1)}</span>
          <span>·</span>
          <span>P90 {stats.p90.toFixed(1)}</span>
        </div>
      )}
    </div>
  );
}
